function _classCallCheck(instance, Constructor) { if (!(instance instanceof Constructor)) { throw new TypeError("Cannot call a class as a function"); } }

function _defineProperties(target, props) { for (var i = 0; i < props.length; i++) { var descriptor = props[i]; descriptor.enumerable = descriptor.enumerable || false; descriptor.configurable = true; if ("value" in descriptor) descriptor.writable = true; Object.defineProperty(target, descriptor.key, descriptor); } }

function _createClass(Constructor, protoProps, staticProps) { if (protoProps) _defineProperties(Constructor.prototype, protoProps); if (staticProps) _defineProperties(Constructor, staticProps); return Constructor; }

/*
 * @copyright Copyright (c) 2019 Julius Härtl <jus@bitgrid.net>
 *
 * @author Julius Härtl <jus@bitgrid.net>
 *
 * @license GNU AGPL version 3 or any later version
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <http://www.gnu.org/licenses/>.
 *
 */
import axios from '@nextcloud/axios';
import { endpointUrl } from '../helpers';
import { ERROR_TYPE } from './SyncService';
import { sendableSteps } from 'prosemirror-collab';
/**
 * Minimum inverval to refetch the document changes
 * @type {number}
 */

var FETCH_INTERVAL = 300;
/**
 * Maximum interval between refetches of document state if multiple users have joined
 * @type {number}
 */

var FETCH_INTERVAL_MAX = 5000;
/**
 * Interval to check for changes when there is only one user joined
 * @type {number}
 */

var FETCH_INTERVAL_SINGLE_EDITOR = 5000;
var MIN_PUSH_RETRY = 500;
var MAX_PUSH_RETRY = 10000;
/* Timeout after that a PUSH_FAILURE error is emitted */

var WARNING_PUSH_RETRY = 5000;
/* Maximum number of retries for fetching before emitting a connection error */

var MAX_RETRY_FETCH_COUNT = 5;
/* Timeout for sessions to be marked as disconnected */

var COLLABORATOR_DISCONNECT_TIME = 20;

var PollingBackend = /*#__PURE__*/function () {
  function PollingBackend(authority) {
    _classCallCheck(this, PollingBackend);

    /** @type SyncService */
    this._authority = authority;
    this.fetchInterval = FETCH_INTERVAL;
    this.retryTime = MIN_PUSH_RETRY;
    this.lock = false;
    this.fetchRetryCounter = 0;
  }

  _createClass(PollingBackend, [{
    key: "connect",
    value: function connect() {
      this.fetcher = setInterval(this._fetchSteps.bind(this), 0);
    }
  }, {
    key: "_isPublic",
    value: function _isPublic() {
      return !!this._authority.options.shareToken;
    }
  }, {
    key: "forceSave",
    value: function forceSave() {
      this._forcedSave = true;
      this.fetchSteps();
    }
  }, {
    key: "save",
    value: function save() {
      this._manualSave = true;
      this.fetchSteps();
    }
  }, {
    key: "fetchSteps",
    value: function fetchSteps() {
      this._fetchSteps();
    }
    /**
     * This method is only called though the timer
     */

  }, {
    key: "_fetchSteps",
    value: function _fetchSteps() {
      var _this = this;

      if (this.lock || !this.fetcher) {
        return;
      }

      this.lock = true;
      var autosaveContent;

      if (this._forcedSave || this._manualSave || !sendableSteps(this._authority.state) && this._authority._getVersion() !== this._authority.document.lastSavedVersion) {
        autosaveContent = this._authority._getContent();
      }

      axios.post(endpointUrl('session/sync', this._isPublic()), {
        documentId: this._authority.document.id,
        sessionId: this._authority.session.id,
        sessionToken: this._authority.session.token,
        version: this._authority._getVersion(),
        autosaveContent: autosaveContent,
        force: !!this._forcedSave,
        manualSave: !!this._manualSave,
        token: this._authority.options.shareToken,
        filePath: this._authority.options.filePath
      }).then(function (response) {
        _this.fetchRetryCounter = 0;

        if (_this._authority.document.lastSavedVersion < response.data.document.lastSavedVersion) {
          console.debug('Saved document', response.data.document);

          _this._authority.emit('save', {
            document: response.data.document,
            sessions: response.data.sessions
          });
        }

        _this._authority.emit('change', {
          document: response.data.document,
          sessions: response.data.sessions
        });

        _this._authority.document = response.data.document;
        _this._authority.sessions = response.data.sessions;

        if (response.data.steps.length === 0) {
          _this.lock = false;

          if (response.data.sessions.filter(function (session) {
            return session.lastContact > Date.now() / 1000 - COLLABORATOR_DISCONNECT_TIME;
          }).length < 2) {
            _this.maximumRefetchTimer();
          } else {
            _this.increaseRefetchTimer();
          }

          _this._authority.emit('stateChange', {
            dirty: false
          });

          _this._authority.emit('stateChange', {
            initialLoading: true
          });

          return;
        }

        _this._authority._receiveSteps(response.data);

        _this.lock = false;
        _this._forcedSave = false;

        _this.resetRefetchTimer();
      }).catch(function (e) {
        _this.lock = false;

        if (!e.response || e.code === 'ECONNABORTED') {
          if (_this.fetchRetryCounter++ >= MAX_RETRY_FETCH_COUNT) {
            console.error('[PollingBackend:fetchSteps] Network error when fetching steps, emitting CONNECTION_FAILED');

            _this._authority.emit('error', ERROR_TYPE.CONNECTION_FAILED, {});
          } else {
            console.error("[PollingBackend:fetchSteps] Network error when fetching steps, retry ".concat(_this.fetchRetryCounter));
          }
        } else if (e.response.status === 409 && e.response.data.document.currentVersion === _this._authority.document.currentVersion) {
          // Only emit conflict event if we have synced until the latest version
          console.error('Conflict during file save, please resolve');

          _this._authority.emit('error', ERROR_TYPE.SAVE_COLLISSION, {
            outsideChange: e.response.data.outsideChange
          });
        } else if (e.response.status === 403) {
          _this._authority.emit('error', ERROR_TYPE.CONNECTION_FAILED, {});
        } else if (e.response.status === 404) {
          _this._authority.emit('error', ERROR_TYPE.SOURCE_NOT_FOUND, {});
        } else if (e.response.status === 503) {
          _this.increaseRefetchTimer();

          _this._authority.emit('error', ERROR_TYPE.CONNECTION_FAILED, {
            retry: true
          });

          console.error('Failed to fetch steps due to unavailable service', e);
        } else {
          _this.increaseRefetchTimer();

          _this._authority.emit('error', ERROR_TYPE.CONNECTION_FAILED, {
            retry: false
          });

          console.error('Failed to fetch steps due to other reason', e);
        }
      });
      this._manualSave = false;
      this._forcedSave = false;
    }
  }, {
    key: "sendSteps",
    value: function sendSteps(_sendable) {
      var _this2 = this;

      this._authority.emit('stateChange', {
        dirty: true
      });

      if (this.lock) {
        setTimeout(function () {
          _this2._authority.sendSteps();
        }, 100);
        return;
      }

      this.lock = true;
      var sendable = typeof _sendable === 'function' ? _sendable() : _sendable;
      var steps = sendable.steps;
      axios.post(endpointUrl('session/push', !!this._authority.options.shareToken), {
        documentId: this._authority.document.id,
        sessionId: this._authority.session.id,
        sessionToken: this._authority.session.token,
        steps: steps.map(function (s) {
          return s.toJSON ? s.toJSON() : s;
        }) || [],
        version: sendable.version,
        token: this._authority.options.shareToken,
        filePath: this._authority.options.filePath
      }).then(function (response) {
        _this2.carefulRetryReset();

        _this2.lock = false;

        _this2.fetchSteps();
      }).catch(function (e) {
        console.error('failed to apply steps due to collission, retrying');
        _this2.lock = false;

        if (!e.response || e.code === 'ECONNABORTED') {
          _this2._authority.emit('error', ERROR_TYPE.CONNECTION_FAILED, {});

          return;
        } else if (e.response.status === 403 && e.response.data.document.currentVersion === _this2._authority.document.currentVersion) {
          // Only emit conflict event if we have synced until the latest version
          _this2._authority.emit('error', ERROR_TYPE.PUSH_FAILURE, {});

          OC.Notification.showTemporary('Changes could not be sent yet');
        }

        _this2.fetchSteps();

        _this2.carefulRetry();
      });
    }
  }, {
    key: "disconnect",
    value: function disconnect() {
      clearInterval(this.fetcher);
      this.fetcher = 0;
    }
  }, {
    key: "resetRefetchTimer",
    value: function resetRefetchTimer() {
      if (this.fetcher === 0) {
        return;
      }

      this.fetchInterval = FETCH_INTERVAL;
      clearInterval(this.fetcher);
      this.fetcher = setInterval(this._fetchSteps.bind(this), this.fetchInterval);
    }
  }, {
    key: "increaseRefetchTimer",
    value: function increaseRefetchTimer() {
      if (this.fetcher === 0) {
        return;
      }

      this.fetchInterval = Math.min(this.fetchInterval * 2, FETCH_INTERVAL_MAX);
      clearInterval(this.fetcher);
      this.fetcher = setInterval(this._fetchSteps.bind(this), this.fetchInterval);
    }
  }, {
    key: "maximumRefetchTimer",
    value: function maximumRefetchTimer() {
      if (this.fetcher === 0) {
        return;
      }

      this.fetchInterval = FETCH_INTERVAL_SINGLE_EDITOR;
      clearInterval(this.fetcher);
      this.fetcher = setInterval(this._fetchSteps.bind(this), this.fetchInterval);
    }
  }, {
    key: "carefulRetry",
    value: function carefulRetry() {
      var newRetry = this.retryTime ? Math.min(this.retryTime * 2, MAX_PUSH_RETRY) : MIN_PUSH_RETRY;

      if (newRetry > WARNING_PUSH_RETRY && this.retryTime < WARNING_PUSH_RETRY) {
        OC.Notification.showTemporary('Changes could not be sent yet');

        this._authority.emit('error', ERROR_TYPE.PUSH_FAILURE, {});
      }

      this.retryTime = newRetry;
    }
  }, {
    key: "carefulRetryReset",
    value: function carefulRetryReset() {
      this.retryTime = MIN_PUSH_RETRY;
    }
  }]);

  return PollingBackend;
}();

export default PollingBackend;